import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';

const META = {
  model: 'gpt-4.1-mini',
  method: 'sac3',
  dataset: 'medhallu',
  samples: 10,
};

const SUMMARY = [
  { label: 'Total questions', value: '10', hint: 'Loaded from the JSON result file' },
  { label: 'Detected hallucinations', value: '0', hint: 'Current detector outputs' },
  { label: 'Ground-truth matches', value: '10/10', hint: '100.0% agreement' },
  { label: 'Avg hallucination score', value: '0.191', hint: 'Mean across all items' },
];

const CONSISTENCY = [
  { label: 'Avg self-consistency', value: '0.787' },
  { label: 'Avg cross-question consistency', value: '0.844' },
  { label: 'Avg cross-model consistency', value: '0.785' },
];

const CASES = [
  {
    id: 1,
    question: 'Do mitochondria play a role in remodelling lace plant leaves during programmed cell death?',
    detected: false,
    halScore: 0.048381,
    selfCons: 0.882,
    crossQ: 0.980,
    crossM: 0.984,
    gtMatch: true,
    answer: `Yes, mitochondria do play a role in remodeling lace plant leaves during programmed cell death (PCD).

The lace plant (Aponogeton madagascariensis) is a well-studied model for understanding PCD because its leaves naturally develop perforations through a tightly regulated cell death process. During this developmental PCD, specific cells in the leaf undergo controlled death to form the characteristic holes, creating the "lace" pattern.

Mitochondria are central players in many forms of PCD, including in plants. In the lace plant, studies have shown that mitochondrial dynamics and function are closely linked to the progression of PCD:

- Mitochondrial membrane potential changes: Early in lace plant PCD, mitochondria exhibit changes in membrane potential, which is a hallmark of mitochondrial involvement in cell death signaling.
- Release of signaling molecules: Mitochondria can release reactive oxygen species (ROS) and other factors that act as signals to promote PCD pathways.
- Energy regulation: Since PCD is an energy-dependent process, mitochondria help regulate the energy supply necessary for the execution of cell death.
- Interaction with other organelles: Mitochondria interact with the nucleus and vacuole to coordinate the dismantling of cellular components during lace plant leaf remodeling.

In summary, mitochondria contribute to the signaling and execution phases of programmed cell death in lace plant leaves, facilitating the precise removal of cells to form the leaf's distinctive perforations.`,
  },
  {
    id: 2,
    question: 'Landolt C and snellen e acuity: differences in strabismus amblyopia?',
    detected: false,
    halScore: 0.377975,
    selfCons: 0.627,
    crossQ: 0.673,
    crossM: 0.549,
    gtMatch: true,
    answer: `Certainly! Here's a comparison of Landolt C and Snellen E acuity tests, particularly in the context of strabismic amblyopia.

Strabismic amblyopia is a form of amblyopia caused by misalignment of the eyes (strabismus), leading to reduced visual acuity in the affected eye.

Landolt C vs Snellen E: Basic Differences
- Landolt C uses a ring with a gap in one of 8 directions; task is to identify the gap direction.
- Snellen E uses the letter "E" oriented in 4 directions; task is to identify the orientation.

Differences in Strabismic Amblyopia:
1. Sensitivity to Crowding: Landolt C may be more sensitive to crowding effects common in strabismic amblyopia.
2. Directional Discrimination: Landolt C requires 8-direction discrimination vs 4 for Snellen E — more complex in amblyopia.
3. Test Reliability: Landolt C is a gold standard in research; Snellen E is more commonly used clinically.
4. Visual Acuity Scores: Studies show Landolt C can reveal worse acuity than Snellen E in amblyopic eyes.

Clinical Implication: For detailed assessment, Landolt C provides a more sensitive measure; for routine screening, Snellen E is practical but may underestimate severity.`,
  },
  {
    id: 3,
    question: 'Syncope during bathing in infants, a pediatric form of water-induced urticaria?',
    detected: false,
    halScore: 0.301386,
    selfCons: 0.675,
    crossQ: 0.768,
    crossM: 0.629,
    gtMatch: true,
    answer: `Syncope during bathing in infants is a rare but recognized phenomenon that can sometimes be associated with a form of water-induced urticaria, although the two are distinct clinical entities.

Water-Induced Urticaria (Aquagenic Urticaria): A rare physical urticaria triggered by contact with water, presenting as pruritic wheals shortly after water exposure.

Syncope During Bathing in Infants: Possible mechanisms include reflex (vasovagal) syncope triggered by warm water immersion, or in very rare cases, water-induced anaphylaxis.

Conclusion: Not exactly — syncope during bathing is generally considered a reflex syncope or vasovagal response rather than a direct manifestation of water-induced urticaria. Water-induced urticaria primarily causes skin symptoms, and systemic symptoms like syncope are not typical.`,
  },
  {
    id: 4,
    question: 'Are the long-term results of the transanal pull-through equal to those of the transabdominal pull-through?',
    detected: false,
    halScore: 0.128698,
    selfCons: 0.766,
    crossQ: 0.872,
    crossM: 0.976,
    gtMatch: true,
    answer: `The long-term results of the transanal pull-through procedure for Hirschsprung disease are generally considered comparable to those of the traditional transabdominal pull-through in terms of functional outcomes, including bowel function and continence.

Key findings:
- Bowel function and continence: Both approaches yield similar long-term bowel function with many patients achieving good continence.
- Complications: No significant difference in postoperative enterocolitis, constipation, or incontinence has been consistently demonstrated in long-term follow-up.
- Quality of life: Long-term quality of life assessments tend to be similar between the two approaches.

Summary: Current evidence supports that the transanal pull-through provides long-term functional outcomes equivalent to the transabdominal pull-through. Choice of procedure often depends on surgeon experience, patient anatomy, and institutional preference.`,
  },
  {
    id: 5,
    question: 'Can tailored interventions increase mammography use among HMO women?',
    detected: false,
    halScore: 0.043030,
    selfCons: 0.968,
    crossQ: 0.947,
    crossM: 0.960,
    gtMatch: true,
    answer: `Yes, tailored interventions can increase mammography use among women enrolled in Health Maintenance Organizations (HMOs). Research has shown that personalized communication strategies — such as customized letters, phone calls, or educational materials that address individual risk factors, barriers, and motivational factors — are more effective in encouraging mammography screening compared to generic reminders.

Tailored interventions work by:
- Addressing personal barriers: Directly responding to specific concerns or misconceptions.
- Enhancing relevance: Personalized messages are more engaging and perceived as more relevant.
- Improving motivation: Highlighting individual risk and benefits motivates women who might otherwise delay screening.

Studies within HMO settings have demonstrated that women receiving tailored reminders or counseling had higher rates of mammography uptake than those receiving standard care.`,
  },
  {
    id: 6,
    question: 'Double balloon enteroscopy: is it efficacious and safe in a community setting?',
    detected: false,
    halScore: 0.257510,
    selfCons: 0.675,
    crossQ: 0.874,
    crossM: 0.635,
    gtMatch: true,
    answer: `Double balloon enteroscopy (DBE) is an advanced endoscopic technique designed to visualize and intervene in the small intestine using two balloons that are alternately inflated and deflated.

Efficacy in community settings:
- DBE has high diagnostic yield (approx. 60–80%) for obscure GI bleeding, small bowel tumors, Crohn's disease, and polyps.
- Published data suggest that with appropriate training, diagnostic and therapeutic yields are comparable to academic centers.

Safety in community settings:
- DBE is generally safe with a low complication rate (typically less than 1–2%).
- Complications include pancreatitis, perforation, bleeding, and sedation-related adverse events.
- Safety profiles remain favorable when performed by trained endoscopists with appropriate patient selection.

Conclusion: Double balloon enteroscopy is both efficacious and safe in community settings when performed by trained endoscopists.`,
  },
  {
    id: 7,
    question: '30-Day and 1-year mortality in emergency general surgery laparotomies: an area of concern and need for improvement?',
    detected: false,
    halScore: 0.070088,
    selfCons: 0.872,
    crossQ: 0.960,
    crossM: 0.948,
    gtMatch: true,
    answer: `Emergency laparotomies are high-risk procedures with significantly higher mortality rates compared to elective surgeries.

Key statistics:
- 30-day mortality: Typically ranges 10% to 25%, depending on patient factors and surgical indication.
- 1-year mortality: Can exceed 30–40%, reflecting comorbidities, postoperative complications, and functional decline.

Contributing factors: Advanced age, frailty, comorbidities, severity of underlying pathology, delays in diagnosis, and variability in healthcare resources.

Need for improvement:
- Early identification and optimization using rapid assessment tools and sepsis bundles.
- Multidisciplinary care involving surgeons, intensivists, geriatricians, and allied health professionals.
- Enhanced recovery pathways tailored to emergency surgery patients.
- National and international registries (e.g., NELA in the UK) to benchmark outcomes and identify quality improvement areas.`,
  },
  {
    id: 8,
    question: 'Is adjustment for reporting heterogeneity necessary in sleep disorders?',
    detected: false,
    halScore: 0.032466,
    selfCons: 0.973,
    crossQ: 0.965,
    crossM: 0.966,
    gtMatch: true,
    answer: `Adjustment for reporting heterogeneity can be important in studies of sleep disorders, depending on the context and type of data being analyzed.

What is reporting heterogeneity? Differences in how individuals or groups perceive, interpret, and report their symptoms due to cultural factors, language, education, or personal thresholds.

Why it matters in sleep disorders:
- Many sleep disorder assessments rely on self-reported symptoms (insomnia severity, daytime sleepiness).
- When comparing across demographic or cultural groups, unadjusted self-reports may reflect reporting styles rather than true differences.
- In large-scale epidemiological surveys, self-report is common, increasing potential for bias.

Methods: Anchoring vignettes, statistical models for differential item functioning, objective sleep measures.

Conclusion: Adjustment is often necessary in epidemiological research using self-reported data, especially when comparing diverse populations. In studies using objective diagnostic tools, this adjustment may be less critical.`,
  },
  {
    id: 9,
    question: 'Do mutations causing low HDL-C promote increased carotid intima-media thickness?',
    detected: false,
    halScore: 0.366661,
    selfCons: 0.664,
    crossQ: 0.651,
    crossM: 0.580,
    gtMatch: true,
    answer: `Mutations that cause low HDL-C levels have been studied to understand their impact on carotid intima-media thickness (cIMT), a surrogate marker of subclinical atherosclerosis.

Key points:
1. Low HDL-C and cardiovascular risk: Epidemiological studies associate low HDL-C with increased CVD risk, but causality is debated.
2. Genetic mutations causing low HDL-C: Genes such as ABCA1, LCAT, CETP, APOA1 lead to low HDL-C levels.
3. Evidence from genetic studies: Some Mendelian randomization studies suggest genetically low HDL-C does not necessarily increase CAD risk; others find that mutations causing dysfunctional HDL can be associated with increased atherosclerosis.
4. cIMT findings: Studies examining cIMT in low HDL-C mutation carriers show mixed results — some indicate increased cIMT, others find no significant difference.

Summary: Mutations causing low HDL-C can promote increased cIMT in some cases, particularly when they impair HDL function or biogenesis. Not all low HDL-C mutations are associated with increased cIMT.`,
  },
  {
    id: 10,
    question: "A short stay or 23-hour ward in a general and academic children's hospital: are they effective?",
    detected: false,
    halScore: 0.282541,
    selfCons: 0.766,
    crossQ: 0.755,
    crossM: 0.619,
    gtMatch: true,
    answer: `Short stay or 23-hour wards in children's hospitals are designed to manage pediatric patients requiring observation or treatment between a few hours and a full inpatient admission.

Effectiveness:
1. Reduced Hospital Admissions: Short stay units can effectively manage asthma exacerbations, dehydration, minor infections, and post-procedural observation, reducing need for full inpatient admissions.
2. Improved Patient Flow: Diverting appropriate cases alleviates pressure on inpatient beds and reduces ED overcrowding.
3. Cost-Effectiveness: Minimizes unnecessary admissions and associated inpatient expenses.
4. Patient and Family Satisfaction: Shorter stays in a family-centered environment improve satisfaction and reduce stress.
5. Clinical Outcomes: When properly staffed, short stay wards maintain safety and quality of care comparable to inpatient units, with low readmission rates.

Summary: Short stay or 23-hour wards are generally effective for selected pediatric cases, improving hospital efficiency, reducing costs, and maintaining high-quality care.`,
  },
];

const SORT_KEYS = {
  0: 'id',
  1: 'question',
  2: 'detected',
  3: 'halScore',
  4: 'selfCons',
  5: 'crossQ',
  6: 'crossM',
  7: 'gtMatch',
};

function SortIcon({ col, sortState }) {
  if (sortState.col !== col) return <ChevronsUpDown className="inline w-3 h-3 ml-1 text-gray-300" />;
  return sortState.asc
    ? <ChevronUp className="inline w-3 h-3 ml-1 text-blue-500" />
    : <ChevronDown className="inline w-3 h-3 ml-1 text-blue-500" />;
}

function ScoreBar({ value }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2.5 bg-gray-100 border border-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-blue-400 to-red-400"
          style={{ width: `${(value * 100).toFixed(1)}%` }}
        />
      </div>
      <span className="text-gray-700 text-sm tabular-nums">{value.toFixed(3)}</span>
    </div>
  );
}

export default function HallucinationResultsDashboard() {
  const [search, setSearch] = useState('');
  const [gtFilter, setGtFilter] = useState('all');
  const [detFilter, setDetFilter] = useState('all');
  const [sortState, setSortState] = useState({ col: 0, asc: true });
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSort = (col) => {
    setSortState(prev => ({ col, asc: prev.col === col ? !prev.asc : true }));
  };

  const filtered = useMemo(() => {
    let rows = CASES.filter(c => {
      const text = `${c.question} ${c.answer}`.toLowerCase();
      const okSearch = !search || text.includes(search.toLowerCase());
      const okGt = gtFilter === 'all' || String(c.gtMatch) === gtFilter;
      const okDet = detFilter === 'all' || String(c.detected) === detFilter;
      return okSearch && okGt && okDet;
    });

    const key = SORT_KEYS[sortState.col];
    rows = [...rows].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number') return sortState.asc ? av - bv : bv - av;
      if (typeof av === 'boolean') return sortState.asc ? Number(av) - Number(bv) : Number(bv) - Number(av);
      return sortState.asc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return rows;
  }, [search, gtFilter, detFilter, sortState]);

  const headers = [
    { label: '#', col: 0 },
    { label: 'Question', col: 1 },
    { label: 'Detector label', col: 2 },
    { label: 'Hallucination score', col: 3 },
    { label: 'Self-consistency', col: 4 },
    { label: 'Cross-question', col: 5 },
    { label: 'Cross-model', col: 6 },
    { label: 'Ground truth', col: 7 },
  ];

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-[1240px] mx-auto">

        {/* Page title */}
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Hallucination Detection Results Dashboard</h1>

        {/* Hero */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <p className="text-gray-500 text-sm leading-relaxed mb-4">
            Review model outputs, detector scores, and ground-truth agreement from the saved run result.
          </p>
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(META).map(([k, v]) => (
              <span key={k} className="bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-700">
                <strong className="capitalize">{k}:</strong> {v}
              </span>
            ))}
          </div>

          {/* Stats row 1 */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            {SUMMARY.map(s => (
              <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">{s.label}</div>
                <div className="text-3xl font-extrabold text-gray-900 mt-2">{s.value}</div>
                <div className="text-xs text-gray-400 mt-1">{s.hint}</div>
              </div>
            ))}
          </div>

          {/* Stats row 2 */}
          <div className="grid grid-cols-4 gap-4">
            {CONSISTENCY.map(s => (
              <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider font-medium">{s.label}</div>
                <div className="text-3xl font-extrabold text-gray-900 mt-2">{s.value}</div>
              </div>
            ))}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <div className="text-xs text-blue-600 uppercase tracking-wider font-medium mb-2">Quick read</div>
              <div className="text-xs text-gray-500 leading-relaxed">
                Use search, filter, and sort by score. Click a row to expand the full predicted answer.
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
          <div className="flex flex-wrap gap-2">
            <input
              type="search"
              placeholder="Search questions or answers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 min-w-[240px] focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <select
              value={gtFilter}
              onChange={e => setGtFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="all">All GT states</option>
              <option value="true">Ground-truth matched</option>
              <option value="false">Ground-truth not matched</option>
            </select>
            <select
              value={detFilter}
              onChange={e => setDetFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="all">All detector labels</option>
              <option value="true">Hallucination</option>
              <option value="false">Not hallucination</option>
            </select>
          </div>
          <span className="text-xs text-gray-400 border border-gray-200 rounded-full px-3 py-1.5">
            Click a column header to sort
          </span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {headers.map(h => (
                  <th
                    key={h.col}
                    onClick={() => handleSort(h.col)}
                    className="px-3 py-3 text-left text-xs text-gray-500 font-semibold uppercase tracking-wider cursor-pointer hover:text-blue-600 select-none whitespace-nowrap"
                  >
                    {h.label}
                    <SortIcon col={h.col} sortState={sortState} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <>
                  <tr
                    key={c.id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleExpand(c.id)}
                  >
                    <td className="px-3 py-3 text-gray-400 align-top">{c.id}</td>
                    <td className="px-3 py-3 align-top max-w-xs">
                      <div className="font-semibold text-gray-800 leading-snug line-clamp-2">{c.question}</div>
                    </td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                      {c.detected
                        ? <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-200">Hallucination</span>
                        : <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">Not hallucination</span>
                      }
                    </td>
                    <td className="px-3 py-3 align-top">
                      <ScoreBar value={c.halScore} />
                    </td>
                    <td className="px-3 py-3 align-top text-gray-700 tabular-nums">{c.selfCons.toFixed(3)}</td>
                    <td className="px-3 py-3 align-top text-gray-700 tabular-nums">{c.crossQ.toFixed(3)}</td>
                    <td className="px-3 py-3 align-top text-gray-700 tabular-nums">{c.crossM.toFixed(3)}</td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                      {c.gtMatch
                        ? <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">Matched</span>
                        : <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-200">Not matched</span>
                      }
                    </td>
                  </tr>

                  {expanded[c.id] && (
                    <tr key={`${c.id}-detail`} className="bg-slate-50 border-b border-gray-200">
                      <td colSpan={8} className="px-4 py-4">
                        <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 2fr' }}>
                          <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <div className="text-xs text-blue-600 uppercase tracking-wider font-semibold mb-2">Question</div>
                            <p className="text-sm text-gray-700 leading-relaxed">{c.question}</p>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <div className="text-xs text-blue-600 uppercase tracking-wider font-semibold mb-2">Predicted answer</div>
                            <div className="max-h-64 overflow-y-auto">
                              <pre className="text-xs text-gray-600 whitespace-pre-wrap break-words leading-relaxed font-sans">
                                {c.answer}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-400 mt-3">
          This report is fully static — all data is embedded from the saved run result file.
        </p>
      </div>
    </div>
  );
}
